/**
 * usePauseQuizState
 * Pause & Quiz 상태 집계 훅 — effectiveMode 산출 · pending 답 Map.
 *
 * Spec: docs/superpowers/specs/2026-04-22-lc-pause-quiz-panel-redesign-design.md §5, §7.6
 */
import { useCallback, useMemo, useState } from 'react'
import type {
  LcVideoDataResponse,
  PauseAndGoPoint,
  PauseQuizResponseSummary,
} from '@/api/lcLearning'

export type PauseMode = 'guide' | 'solve'
export type PausePointStatus = 'done' | 'current' | 'pending'

export interface UsePauseQuizStateArgs {
  videoData: LcVideoDataResponse
  isFirstAttempt: boolean
  activePoint: PauseAndGoPoint | null
  previousResponses: PauseQuizResponseSummary[]
  /** 교사 지정 복습 레슨 — true 이면 guide 모드/토글 무시하고 항상 solve 강제. */
  isDesignatedReview?: boolean
}

export interface PauseQuizState {
  // 파생 상태
  effectiveMode: PauseMode
  isCurrentSubmitted: boolean
  currentPointResponse: PauseQuizResponseSummary | null

  // pending 답 Map (quizId 기준)
  pendingAnswersByQuizId: Map<string, string[]>
  setPendingAnswer: (quizId: string, answers: string[]) => void
  clearPendingAnswer: (quizId: string) => void

  // PointList 유틸
  statusFor: (point: PauseAndGoPoint) => PausePointStatus
  indexOf: (point: PauseAndGoPoint) => number
}

export function usePauseQuizState({
  videoData,
  isFirstAttempt,
  activePoint,
  previousResponses,
  isDesignatedReview = false,
}: UsePauseQuizStateArgs): PauseQuizState {
  const [pendingAnswersByQuizId, setPendingMap] = useState<Map<string, string[]>>(new Map())

  // 최초 학습은 교재 풀이만 제공한다. 재학습과 지정 복습은 기존 온라인 풀이를 유지한다.
  const effectiveMode: PauseMode = isDesignatedReview || !isFirstAttempt ? 'solve' : 'guide'

  const currentPointResponse = useMemo<PauseQuizResponseSummary | null>(() => {
    if (!activePoint) return null
    return (
      previousResponses.find(
        (r) => r.pauseTimeMs === activePoint.time && r.quizId === String(activePoint.quizId ?? ''),
      ) ?? null
    )
  }, [activePoint, previousResponses])

  const isCurrentSubmitted = !!currentPointResponse

  const setPendingAnswer = useCallback((quizId: string, answers: string[]) => {
    setPendingMap((prev) => {
      const next = new Map(prev)
      next.set(quizId, answers)
      return next
    })
  }, [])

  const clearPendingAnswer = useCallback((quizId: string) => {
    setPendingMap((prev) => {
      const next = new Map(prev)
      next.delete(quizId)
      return next
    })
  }, [])

  const points = useMemo(
    () => videoData.pauseAndGoPoints ?? [],
    [videoData.pauseAndGoPoints],
  )

  const statusFor = useCallback(
    (point: PauseAndGoPoint): PausePointStatus => {
      if (previousResponses.some(
        (r) => r.pauseTimeMs === point.time && r.quizId === String(point.quizId ?? ''),
      )) return 'done'
      if (activePoint?.time === point.time && activePoint.quizId === point.quizId) return 'current'
      return 'pending'
    },
    [previousResponses, activePoint],
  )

  const indexOf = useCallback(
    (point: PauseAndGoPoint): number => {
      return points.findIndex((p) => p.time === point.time && p.quizId === point.quizId)
    },
    [points],
  )

  return {
    effectiveMode,
    isCurrentSubmitted,
    currentPointResponse,
    pendingAnswersByQuizId,
    setPendingAnswer,
    clearPendingAnswer,
    statusFor,
    indexOf,
  }
}
